import { useEffect, useRef, useState } from 'react'
import { api } from '@carrent/api'
import { preview as platformPreview } from '@carrent/platform'
import {
  DAMAGE_TYPE_LABELS,
  DamageSchema,
  SEVERITIES,
  damageTypesFor,
  truckProjections,
} from '@carrent/ui'
import { ContextHelp } from './kb/ContextHelp'

/**
 * Шаг «Повреждения» на приёмке.
 *
 * Схему поддерживают не все типы кузова: у машины может быть
 * не проставлен body_type или стоять кузов, которого мы ещё
 * не нарисовали. Тогда шаг честно об этом говорит и пропускается —
 * приёмка идёт по фотографиям, как раньше. Молча прятать шаг
 * нельзя: водитель должен понимать, почему схемы нет.
 */

const uuid = () =>
  globalThis.crypto?.randomUUID?.() ??
  `m-${Date.now()}-${Math.random().toString(36).slice(2)}`

// client_mark_id уходит в ключ объекта в хранилище, поэтому
// набор символов тот же, что сервер принимает: [a-zA-Z0-9-]{8,64}.
const markId = () => uuid().replace(/[^a-zA-Z0-9-]/g, '').slice(0, 40)

function TypeChips({ material, value, onChange }) {
  return (
    <div className="damage-sheet__chips">
      {damageTypesFor(material).map((type) => (
        <button
          key={type}
          type="button"
          className={`damage-chip${value === type ? ' is-active' : ''}`}
          onClick={() => onChange(type)}
        >
          {DAMAGE_TYPE_LABELS[type]}
        </button>
      ))}
    </div>
  )
}

/** Шторка новой точки: тип, критичность, обязательное фото. */
function NewMarkSheet({ zone, onCancel, onSave }) {
  const [type, setType] = useState(null)
  const [severity, setSeverity] = useState('minor')
  const [file, setFile] = useState(null)
  const inputRef = useRef(null)

  const preview = file ? platformPreview.objectUrl(file) : null
  // Без фото точку не сохранить: то же правило стоит на сервере,
  // здесь оно только для того, чтобы не тратить время водителя.
  const canSave = !!type && !!file

  return (
    <div className="damage-sheet" role="dialog" aria-modal="true" aria-label={zone.label}>
      <div className="damage-sheet__grip" />
      <p className="damage-sheet__zone">{zone.label}</p>

      <p className="damage-sheet__section">Что с деталью</p>
      <TypeChips material={zone.material} value={type} onChange={setType} />

      <p className="damage-sheet__section">Можно ли ехать</p>
      <div className="damage-sheet__chips">
        {SEVERITIES.map((s) => (
          <button
            key={s.value}
            type="button"
            className={`damage-chip${severity === s.value ? ' is-active' : ''}${
              s.value === 'critical' ? ' damage-chip--critical' : ''}`}
            onClick={() => setSeverity(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <p className="damage-sheet__section">Фото повреждения</p>
      {preview ? (
        <button type="button" className="damage-sheet__photo" onClick={() => inputRef.current?.click()}>
          <img src={preview} alt="Фото повреждения" className="damage-sheet__photo-img" />
          <span className="damage-sheet__photo-retake">Переснять</span>
        </button>
      ) : (
        <button type="button" className="damage-sheet__shoot" onClick={() => inputRef.current?.click()}>
          Сделать фото
        </button>
      )}
      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          const picked = e.target.files?.[0]
          e.target.value = ''
          if (picked) setFile(picked)
        }}
      />
      {!file && (
        <p className="damage-sheet__note">Без фото повреждение не зафиксировать.</p>
      )}

      <div className="damage-sheet__actions">
        <button type="button" className="btn btn--outline" onClick={onCancel}>
          Отмена
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!canSave}
          onClick={() => onSave({ type, severity, file })}
        >
          Сохранить
        </button>
      </div>
    </div>
  )
}

/** Шторка существующей точки: своя — удалить, старая — подтвердить. */
function ExistingMarkSheet({ mark, zoneLabelText, onClose, onRemove, onConfirm }) {
  const isOld = mark.status !== 'new'
  return (
    <div className="damage-sheet" role="dialog" aria-modal="true" aria-label={zoneLabelText}>
      <div className="damage-sheet__grip" />
      <p className="damage-sheet__zone">{zoneLabelText}</p>
      <p className="damage-sheet__note">
        {isOld
          ? 'Зафиксировано раньше. Отмечать заново не нужно.'
          : DAMAGE_TYPE_LABELS[mark.damage_type] || 'Новое повреждение'}
      </p>

      {mark.photo_url && (
        <img src={mark.photo_url} alt="Фото повреждения" className="damage-sheet__photo-img" />
      )}
      {mark.previewUrl && !mark.photo_url && (
        <img src={mark.previewUrl} alt="Фото повреждения" className="damage-sheet__photo-img" />
      )}

      <div className="damage-sheet__actions">
        <button type="button" className="btn btn--outline" onClick={onClose}>
          Закрыть
        </button>
        {isOld ? (
          !mark.confirmed && (
            <button type="button" className="btn btn--primary" onClick={onConfirm}>
              Повреждение на месте
            </button>
          )
        ) : (
          <button type="button" className="btn btn--danger" onClick={onRemove}>
            Удалить точку
          </button>
        )}
      </div>
    </div>
  )
}

export default function DamageStep({ car, marks, onChange }) {
  const [projectionKey, setProjectionKey] = useState('front')
  const [passport, setPassport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)

  const projections = truckProjections(car.body_type,
    { hasTailLift: car.has_tail_lift })
  const build = !!projections

  useEffect(() => {
    if (!build) { setLoading(false); return }
    let alive = true
    api.get(`/cars/${car.id}/damages`)
      .then(({ data }) => { if (alive) setPassport(data.data?.damages || []) })
      .catch(() => {
        // Паспорт не догрузился — это не повод срывать приёмку.
        // Водитель отметит всё как новое, диспетчер разберётся.
        if (alive) setError('Не удалось загрузить прежние повреждения. Отметьте новые — это не помешает выезду.')
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [car.id, build])

  if (!build) {
    return (
      <div className="damage-step">
        <div className="alert alert--info">
          {car.body_type && car.body_type !== 'other'
            ? `Для кузова «${car.body_type_label || car.body_type}» схема повреждений пока не нарисована.`
            : 'У машины не указан тип кузова, поэтому схема недоступна.'}
          {' '}Осмотрите машину и приложите фото на предыдущих шагах.
        </div>
      </div>
    )
  }

  const confirmedIds = new Set(marks.filter((m) => m.car_damage_id).map((m) => m.car_damage_id))

  // Старые точки паспорта показываем янтарными; свои новые — красными.
  const schemaMarks = [
    ...(passport || []).map((d) => ({
      id: `old-${d.id}`,
      car_damage_id: d.id,
      zone_id: d.zone_id,
      x: d.x, y: d.y,
      status: 'existing',
      damage_type: d.damage_type,
      photo_url: d.photo_url,
      confirmed: confirmedIds.has(d.id),
    })),
    ...marks.filter((m) => m.status === 'new'),
  ]

  const addMark = (zone, point, { type, severity, file }) => {
    onChange([...marks, {
      client_mark_id: markId(),
      status: 'new',
      zone_id: zone.id,
      x: point.x, y: point.y,
      damage_type: type,
      severity,
      file,
      previewUrl: platformPreview.objectUrl(file),
      zoneLabel: zone.label,
      client_occurred_at: new Date().toISOString(),
    }])
    setSheet(null)
  }

  const confirmOld = (mark) => {
    onChange([...marks, {
      client_mark_id: markId(),
      status: 'confirmed_existing',
      car_damage_id: mark.car_damage_id,
      zone_id: mark.zone_id,
      x: mark.x, y: mark.y,
      client_occurred_at: new Date().toISOString(),
    }])
    setSheet(null)
  }

  const removeMark = (clientMarkId) => {
    onChange(marks.filter((m) => m.client_mark_id !== clientMarkId))
    setSheet(null)
  }

  const criticalCount = marks.filter((m) => m.severity === 'critical').length

  return (
    <div className="damage-step">
      {/* Регламент фотофиксации — здесь же: уходить за ним в базу
          знаний означало бы бросить начатую приёмку. */}
      <ContextHelp query="повреждения фотофиксация" label="Как отмечать повреждения" />

      <DamageSchema
        projections={projections}
        activeKey={projectionKey}
        onSelectProjection={setProjectionKey}
        marks={schemaMarks}
        loading={loading}
        onZoneTap={(zone, point) => setSheet({ kind: 'new', zone, point })}
        onMarkTap={(mark) => setSheet({ kind: 'existing', mark })}
      />

      {error && <div className="alert alert--warning">{error}</div>}

      {marks.length === 0 ? (
        <p className="damage-step__empty">
          Повреждений не отмечено. Если машина целая — переходите дальше.
        </p>
      ) : (
        <p className="damage-step__count">
          Отмечено: {marks.length}
          {criticalCount > 0 && (
            <span className="damage-step__critical"> · критичных: {criticalCount}</span>
          )}
        </p>
      )}

      {sheet?.kind === 'new' && (
        <>
          <button
            type="button"
            className="damage-sheet__scrim"
            aria-label="Закрыть"
            onClick={() => setSheet(null)}
          />
          <NewMarkSheet
            zone={sheet.zone}
            onCancel={() => setSheet(null)}
            onSave={(payload) => addMark(sheet.zone, sheet.point, payload)}
          />
        </>
      )}

      {sheet?.kind === 'existing' && (
        <>
          <button
            type="button"
            className="damage-sheet__scrim"
            aria-label="Закрыть"
            onClick={() => setSheet(null)}
          />
          <ExistingMarkSheet
            mark={sheet.mark}
            zoneLabelText={sheet.mark.zoneLabel || 'Повреждение'}
            onClose={() => setSheet(null)}
            onRemove={() => removeMark(sheet.mark.client_mark_id)}
            onConfirm={() => confirmOld(sheet.mark)}
          />
        </>
      )}
    </div>
  )
}
